import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography, Button, Alert } from '@mui/material';
import { createApiRequest } from '../../utils/apiUtils';

interface SubscriptionLite {
  entitled: boolean;
  status: string;
}

/**
 * 有効なサブスクリプション（entitled）が無い店舗で、店内注文管理の利用を抑止するガード。
 * INACTIVE/EXPIRED 等（entitled=false）の場合は、サブスク有効化を促す画面を表示する。
 * TRIAL/ACTIVE/GRACE/EXEMPT（entitled=true）は通す。
 * 最終的な強制は BE が担い、本ガードは画面遷移の抑止（UX）を担う。feast-fe ADR 0027。
 */
const RequireActiveSubscription: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { store_id } = useParams<{ store_id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'allowed' | 'blocked' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    if (!store_id) {
      setState('error');
      return;
    }
    createApiRequest<SubscriptionLite>(`/stores/${store_id}/subscription`, { method: 'GET' })
      .then((d) => {
        if (active) setState(d.entitled ? 'allowed' : 'blocked');
      })
      .catch(() => {
        if (active) setState('error');
      });
    return () => {
      active = false;
    };
  }, [store_id]);

  if (state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state === 'error') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">サブスクリプション状態の取得に失敗しました。時間をおいて再度お試しください。</Alert>
      </Box>
    );
  }

  if (state === 'blocked') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Paper
          elevation={0}
          sx={{ p: 3, maxWidth: 480, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            サブスクリプションが必要です
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            店内注文管理をご利用いただくには、サブスクリプションを有効化してください。
          </Typography>
          <Button variant="contained" onClick={() => navigate(`/${store_id}/manage/store`)}>
            サブスクリプションを有効化する
          </Button>
        </Paper>
      </Box>
    );
  }

  return children;
};

export default RequireActiveSubscription;
